/**
 * Business Message Models with Industry-Standard Data
 * Based on voice actor valuation and pricing structures
 */

// Import centralized message constants
import { MESSAGE_TYPES } from '../../../constants/messageConstants';

// Industry rate calculation utilities
export const RATE_CALCULATIONS = {
  // Base rates by experience level (Per Finished Hour)
  EXPERIENCE_RATES: {
    BEGINNER: { min: 150, max: 300 },
    INTERMEDIATE: { min: 300, max: 500 },
    PROFESSIONAL: { min: 500, max: 800 },
    CELEBRITY: { min: 1000, max: 2500 }
  },

  // Complexity multipliers
  MULTIPLIERS: {
    accents: 1.25,
    multipleAgeRanges: 1.5,
    complexEmotional: 1.3,
    exclusivity: 1.75,
    rushDeadline: 1.4,
    characterCount: (count) => 1 + ((count - 1) * 0.15) // +15% per additional character
  },

  // Calculate total rate with complexity factors
  calculateRate: (baseRate, factors = {}) => {
    let totalRate = baseRate;

    if (factors.accents) totalRate *= RATE_CALCULATIONS.MULTIPLIERS.accents;
    if (factors.multipleAgeRanges) totalRate *= RATE_CALCULATIONS.MULTIPLIERS.multipleAgeRanges;
    if (factors.complexEmotional) totalRate *= RATE_CALCULATIONS.MULTIPLIERS.complexEmotional;
    if (factors.exclusivity) totalRate *= RATE_CALCULATIONS.MULTIPLIERS.exclusivity;
    if (factors.rushDeadline) totalRate *= RATE_CALCULATIONS.MULTIPLIERS.rushDeadline;
    if (factors.characterCount > 1) totalRate *= RATE_CALCULATIONS.MULTIPLIERS.characterCount(factors.characterCount);

    return Math.round(totalRate);
  }
};

// Test User Profiles with realistic industry data
export const TEST_USER_PROFILES = {
  'premium-voice-actor': {
    userId: 'va-premium-001',
    name: 'Victoria Sterling',
    email: 'victoria@sterlingvoice.com',
    role: 'VOICE_ACTOR',
    experienceLevel: 'PROFESSIONAL',
    baseRate: 650,
    completedProjects: 47,
    rating: 4.8,
    specialties: ['Fantasy', 'Romance', 'Multiple Characters', 'Accents'],
    portfolio: {
      demoReel: 'https://example.com/victoria-sterling-demo.mp3',
      awards: ['Audie Award Finalist 2023', 'Voice Arts Award Winner'],
      notableWorks: ['The Dragon Chronicles', 'Moonlight Romance Series']
    },
    availability: 'SELECTIVE',
    preferredGenres: ['Fantasy', 'Romance', 'Literary Fiction']
  },

  'intermediate-voice-actor': {
    userId: 'va-inter-002',
    name: 'Jake Morrison',
    email: 'jake.morrison@voicetalent.com',
    role: 'VOICE_ACTOR',
    experienceLevel: 'INTERMEDIATE',
    baseRate: 375,
    completedProjects: 12,
    rating: 4.3,
    specialties: ['Adventure', 'Young Adult', 'Contemporary'],
    portfolio: {
      demoReel: 'https://example.com/jake-morrison-demo.mp3',
      notableWorks: ['Teen Adventure Series', 'Modern Romance Collection']
    },
    availability: 'OPEN',
    preferredGenres: ['Young Adult', 'Adventure', 'Contemporary']
  },

  'beginner-voice-actor': {
    userId: 'va-begin-003',
    name: 'Emma Rodriguez',
    email: 'emma.r.voice@gmail.com',
    role: 'VOICE_ACTOR',
    experienceLevel: 'BEGINNER',
    baseRate: 225,
    completedProjects: 3,
    rating: 4.1,
    specialties: ['Contemporary', 'Romance'],
    portfolio: {
      demoReel: 'https://example.com/emma-rodriguez-demo.mp3',
      training: ['Voice Acting Academy Graduate', 'Audiobook Narration Certification']
    },
    availability: 'EAGER',
    preferredGenres: ['Romance', 'Contemporary', 'Self-Help']
  },

  'indie-book-creator': {
    userId: 'bc-indie-001',
    name: 'Sarah Chen',
    email: 'sarah@chenbooks.com',
    role: 'BOOK_CREATOR',
    publishedBooks: 8,
    budget: 'MODERATE', // $5K-15K per book
    preferredGenres: ['Fantasy', 'Adventure'],
    qualityFocus: 'HIGH',
    portfolio: {
      bestSellers: ['The Mystic Realms', 'Shadow Warriors'],
      averageBookLength: '12 hours',
      targetAudience: 'Adult Fantasy Readers'
    },
    projectHistory: {
      completedAudiobooks: 5,
      averageRating: 4.6,
      repeatCollaborations: 3
    }
  },

  'traditional-publisher': {
    userId: 'bc-trad-001',
    name: 'Marcus Publishing House',
    email: 'acquisitions@marcuspublishing.com',
    role: 'BOOK_CREATOR',
    publishedBooks: 200,
    budget: 'HIGH', // $15K+ per book
    preferredGenres: ['Literary Fiction', 'Biography', 'Historical'],
    qualityFocus: 'PREMIUM',
    portfolio: {
      imprints: ['Marcus Literary', 'Heritage Biographies'],
      averageBookLength: '15 hours',
      targetAudience: 'Literary Fiction Enthusiasts'
    },
    projectHistory: {
      completedAudiobooks: 45,
      averageRating: 4.7,
      awardWinners: 8
    }
  },

  'admin-user': {
    userId: 'admin-001',
    name: 'Alex Thompson',
    email: 'alex@authoratorai.com',
    role: 'ADMIN',
    department: 'Platform Operations',
    permissions: ['ALL'],
    specializations: ['Dispute Resolution', 'Quality Assurance', 'Platform Support']
  }
};

// Realistic opportunity templates
export const OPPORTUNITY_TEMPLATES = {
  'epic-fantasy-series': {
    id: 'opp-fantasy-001',
    title: 'The Crystal Kingdoms - Epic Fantasy Series (Book 1)',
    author: 'Sarah Chen',
    genre: 'Fantasy',
    estimatedFinishedHours: 15,
    bookDetails: {
      wordCount: 120000,
      chapterCount: 24,
      seriesPosition: '1 of 5',
      targetAudience: 'Adult Fantasy'
    },
    characterRequirements: {
      mainCharacters: 3,
      supportingCharacters: 5,
      totalUniqueVoices: 8,
      accentsRequired: ['Scottish Highland', 'Irish Brogue'],
      ageRanges: ['Young Adult', 'Adult', 'Elderly'],
      emotionalRange: 'COMPLEX'
    },
    timeline: {
      deadline: '2025-12-15',
      preferredStartDate: '2025-10-01',
      isRushJob: false
    },
    compensation: {
      type: 'PER_FINISHED_HOUR',
      budgetRange: { min: 8000, max: 12000 },
      exclusivityRequired: true,
      bonusStructure: {
        qualityBonus: 500,
        timelyDelivery: 300,
        seriesCommitment: 1000
      }
    }
  },

  'romance-contemporary': {
    id: 'opp-romance-001',
    title: 'Summer Hearts - Contemporary Romance',
    author: 'Lisa Martinez',
    genre: 'Romance',
    estimatedFinishedHours: 8,
    bookDetails: {
      wordCount: 65000,
      chapterCount: 16,
      seriesPosition: 'Standalone',
      targetAudience: 'Adult Romance'
    },
    characterRequirements: {
      mainCharacters: 2,
      supportingCharacters: 3,
      totalUniqueVoices: 4,
      accentsRequired: [],
      ageRanges: ['Adult'],
      emotionalRange: 'MODERATE'
    },
    timeline: {
      deadline: '2025-11-30',
      preferredStartDate: '2025-10-15',
      isRushJob: false
    },
    compensation: {
      type: 'HYBRID',
      basePayment: 2000,
      royaltyPercentage: 12,
      exclusivityRequired: false
    }
  },

  'biography-premium': {
    id: 'opp-bio-001',
    title: 'Legends of Innovation - Tech Pioneer Biography',
    author: 'Marcus Publishing House',
    genre: 'Biography',
    estimatedFinishedHours: 18,
    bookDetails: {
      wordCount: 145000,
      chapterCount: 20,
      seriesPosition: 'Standalone',
      targetAudience: 'Business/Biography'
    },
    characterRequirements: {
      mainCharacters: 1,
      supportingCharacters: 0,
      totalUniqueVoices: 1,
      accentsRequired: [],
      ageRanges: ['Adult'],
      emotionalRange: 'BASIC'
    },
    timeline: {
      deadline: '2026-03-01',
      preferredStartDate: '2025-11-01',
      isRushJob: false
    },
    compensation: {
      type: 'BUYOUT',
      totalPayment: 15000,
      exclusivityRequired: true,
      bonusStructure: {
        qualityBonus: 1000,
        timelyDelivery: 500
      }
    }
  }
};

// Business message templates with realistic data
export const MESSAGE_TEMPLATES = {
  VOICE_ACTOR_APPLICATION: {
    'premium-application': {
      messageType: 'VOICE_ACTOR_APPLICATION',
      template: (applicant, opportunity, options = {}) => ({
        subject: `Application: ${opportunity.title}`,
        content: `Dear ${options.recipient?.name || opportunity.author},

I'm excited to submit my application for "${opportunity.title}". With ${applicant.completedProjects || 0} completed audiobooks and a ${applicant.rating || 4.0}/5 rating, I believe I'm an excellent fit for this project.

**My Qualifications:**
- ${(applicant.experienceLevel || 'Professional').toLowerCase()} narrator with ${applicant.completedProjects || 0} completed projects
- Specialties: ${applicant.specialties?.join(', ') || applicant.specialty || 'Voice Acting'}
- Notable works: ${applicant.portfolio?.notableWorks?.join(', ') || applicant.notableWorks?.join(', ') || 'Various acclaimed titles'}
- Awards: ${applicant.portfolio?.awards?.join(', ') || applicant.awards?.join(', ') || 'Industry recognition'}

**Project Analysis:**
- Estimated ${opportunity.estimatedFinishedHours || opportunity.estimatedHours || 'TBD'} finished hours
- ${opportunity.characterRequirements?.totalUniqueVoices || opportunity.totalUniqueVoices || 1} unique character voices
- Complexity factors: ${(opportunity.characterRequirements?.accentsRequired?.length > 0) || (opportunity.accentsRequired?.length > 0) ? 'Accent work required' : 'Standard narration'}

**Rate Proposal:**
Based on the project complexity and my experience level, I propose $${RATE_CALCULATIONS.calculateRate(applicant.baseRate || 500, {
          accents: (opportunity.characterRequirements?.accentsRequired?.length > 0) || (opportunity.accentsRequired?.length > 0),
          multipleAgeRanges: (opportunity.characterRequirements?.ageRanges?.length > 1) || (opportunity.ageRanges?.length > 1),
          complexEmotional: (opportunity.characterRequirements?.emotionalRange === 'COMPLEX') || (opportunity.emotionalRange === 'COMPLEX'),
          exclusivity: opportunity.compensation?.exclusivityRequired || opportunity.exclusivityRequired,
          characterCount: opportunity.characterRequirements?.totalUniqueVoices || opportunity.totalUniqueVoices || 1
        })} per finished hour.

Total estimated project value: $${RATE_CALCULATIONS.calculateRate(applicant.baseRate || 500, {
          accents: (opportunity.characterRequirements?.accentsRequired?.length > 0) || (opportunity.accentsRequired?.length > 0),
          multipleAgeRanges: (opportunity.characterRequirements?.ageRanges?.length > 1) || (opportunity.ageRanges?.length > 1),
          complexEmotional: (opportunity.characterRequirements?.emotionalRange === 'COMPLEX') || (opportunity.emotionalRange === 'COMPLEX'),
          exclusivity: opportunity.compensation?.exclusivityRequired || opportunity.exclusivityRequired,
          characterCount: opportunity.characterRequirements?.totalUniqueVoices || opportunity.totalUniqueVoices || 1
        }) * (opportunity.estimatedFinishedHours || opportunity.estimatedHours || 10)}

I'm available to start ${opportunity.timeline?.preferredStartDate || 'immediately'} and can meet your ${opportunity.timeline?.deadline || 'project'} deadline.

Best regards,
${applicant.name}

Demo reel: ${applicant.portfolio?.demoReel || applicant.demoReel || 'Available upon request'}`,
        metadata: {
          applicantId: applicant.userId,
          opportunityId: opportunity.id,
          proposedRate: RATE_CALCULATIONS.calculateRate(applicant.baseRate || 500, {
            accents: (opportunity.characterRequirements?.accentsRequired?.length > 0) || (opportunity.accentsRequired?.length > 0),
            multipleAgeRanges: (opportunity.characterRequirements?.ageRanges?.length > 1) || (opportunity.ageRanges?.length > 1),
            complexEmotional: (opportunity.characterRequirements?.emotionalRange === 'COMPLEX') || (opportunity.emotionalRange === 'COMPLEX'),
            exclusivity: opportunity.compensation?.exclusivityRequired || opportunity.exclusivityRequired,
            characterCount: opportunity.characterRequirements?.totalUniqueVoices || opportunity.totalUniqueVoices || 1
          }),
          estimatedTotal: RATE_CALCULATIONS.calculateRate(applicant.baseRate || 500, {
            accents: (opportunity.characterRequirements?.accentsRequired?.length > 0) || (opportunity.accentsRequired?.length > 0),
            multipleAgeRanges: (opportunity.characterRequirements?.ageRanges?.length > 1) || (opportunity.ageRanges?.length > 1),
            complexEmotional: (opportunity.characterRequirements?.emotionalRange === 'COMPLEX') || (opportunity.emotionalRange === 'COMPLEX'),
            exclusivity: opportunity.compensation?.exclusivityRequired || opportunity.exclusivityRequired,
            characterCount: opportunity.characterRequirements?.totalUniqueVoices || opportunity.totalUniqueVoices || 1
          }) * (opportunity.estimatedFinishedHours || opportunity.estimatedHours || 10),
          complexityFactors: {
            accents: (opportunity.characterRequirements?.accentsRequired?.length > 0) || (opportunity.accentsRequired?.length > 0),
            multipleAgeRanges: (opportunity.characterRequirements?.ageRanges?.length > 1) || (opportunity.ageRanges?.length > 1),
            complexEmotional: (opportunity.characterRequirements?.emotionalRange === 'COMPLEX') || (opportunity.emotionalRange === 'COMPLEX'),
            exclusivity: opportunity.compensation?.exclusivityRequired || opportunity.exclusivityRequired,
            characterCount: opportunity.characterRequirements?.totalUniqueVoices || opportunity.totalUniqueVoices || 1
          }
        }
      })
    }
  },

  APPLICATION_STATUS_UPDATE: {
    'acceptance-with-negotiation': {
      messageType: 'APPLICATION_STATUS_UPDATE',
      template: (creator, applicant, opportunity, negotiatedRate, options = {}) => ({
        subject: `Application Accepted: ${opportunity?.title || 'Narration Project'}`,
        content: `Dear ${applicant?.name || 'Applicant'},

Congratulations! We're pleased to accept your application for "${opportunity?.title || 'the narration project'}".

**Final Terms:**
- Rate: $${negotiatedRate || 'TBD'} per finished hour
- Total project value: $${(negotiatedRate || 0) * (opportunity?.estimatedFinishedHours || opportunity?.estimatedHours || 10)}
- Timeline: ${opportunity?.timeline?.preferredStartDate || opportunity?.preferredStartDate || 'TBD'} - ${opportunity?.timeline?.deadline || opportunity?.deadline || 'TBD'}

**Bonus Structure:**
- Quality bonus (4.5+ rating): $${opportunity?.compensation?.bonusStructure?.qualityBonus || opportunity?.bonusStructure?.qualityBonus || 500}
- Timely delivery bonus: $${opportunity?.compensation?.bonusStructure?.timelyDelivery || opportunity?.bonusStructure?.timelyDelivery || 300}
- Series commitment bonus: $${opportunity?.compensation?.bonusStructure?.seriesCommitment || opportunity?.bonusStructure?.seriesCommitment || 0}

**Payment Schedule:**
- 25% upon contract signing
- 25% at 25% completion
- 25% at 75% completion  
- 25% upon final delivery and approval

**Project Details:**
- Estimated ${opportunity?.estimatedFinishedHours || opportunity?.estimatedHours || 'TBD'} finished hours
- ${opportunity?.characterRequirements?.totalUniqueVoices || opportunity?.totalUniqueVoices || 1} unique character voices
- Genre: ${opportunity?.genre || 'General'}
- Complexity: ${opportunity?.characterRequirements?.emotionalRange || opportunity?.emotionalRange || 'Standard'}

We're excited to work with you on this project!

Best regards,
${creator?.name || options?.explicitSender || 'Project Manager'}`,
        metadata: {
          status: 'ACCEPTED',
          finalRate: negotiatedRate || 0,
          totalValue: (negotiatedRate || 0) * (opportunity?.estimatedFinishedHours || opportunity?.estimatedHours || 10),
          paymentSchedule: 'MILESTONE_BASED',
          bonusStructure: opportunity?.compensation?.bonusStructure || opportunity?.bonusStructure || {},
          projectDetails: {
            estimatedHours: opportunity?.estimatedFinishedHours || opportunity?.estimatedHours || 10,
            characterCount: opportunity?.characterRequirements?.totalUniqueVoices || opportunity?.totalUniqueVoices || 1,
            genre: opportunity?.genre || 'General',
            complexity: opportunity?.characterRequirements?.emotionalRange || opportunity?.emotionalRange || 'Standard'
          }
        }
      })
    }
  },

  FINANCIAL_TRANSACTION: {
    'milestone-payment': {
      messageType: 'FINANCIAL_TRANSACTION',
      template: (project, milestone, amount, options = {}) => ({
        subject: `Milestone Payment Released: ${project?.title || 'Narration Project'}`,
        content: `Payment notification for ${project?.title || 'your narration project'}

**Milestone Completed:** ${milestone?.description || 'Project milestone'}
**Chapters:** ${milestone?.chapters?.length > 0 ? milestone.chapters.join(', ') : 'Various chapters'}
**Payment Amount:** $${amount || 0}
**Quality Rating:** ${milestone?.qualityRating || 4.0}/5.0

**Quality Metrics:**
- Retake rate: ${((milestone?.retakeRate || 0.05) * 100).toFixed(1)}% (Industry avg: 15%)
- Pronunciation accuracy: ${((milestone?.pronunciationAccuracy || 0.95) * 100).toFixed(1)}%
- Emotional consistency: ${milestone?.emotionalConsistency || 4.0}/5.0

**Project Performance:**
- Hours recorded: ${milestone?.hoursRecorded || 'TBD'}
- Chapters completed: ${milestone?.chaptersCompleted || milestone?.chapters?.length || 'Multiple'}
- Overall progress: ${milestone?.progressPercentage || 'On track'}

${(milestone?.qualityBonus || 0) > 0 ? `**Quality Bonus Earned:** $${milestone.qualityBonus}` : ''}

**Payment Details:**
- Transaction ID: ${milestone?.transactionId || `TXN-${Date.now()}`}
- Payment method: ${milestone?.paymentMethod || 'Direct deposit'}
- Processing time: 2-3 business days

Payment has been released from escrow and should appear in your account within 2-3 business days.

Thank you for your excellent work on this project!

Best regards,
${options?.sender?.name || options?.explicitSender || 'Payment Processing Team'}`,
        metadata: {
          transactionType: 'MILESTONE_PAYMENT',
          amount: amount || 0,
          milestone: milestone?.phase || milestone?.description || 'Project milestone',
          qualityMetrics: {
            retakeRate: milestone?.retakeRate || 0.05,
            pronunciationAccuracy: milestone?.pronunciationAccuracy || 0.95,
            emotionalConsistency: milestone?.emotionalConsistency || 4.0,
            qualityRating: milestone?.qualityRating || 4.0
          },
          bonusEarned: milestone?.qualityBonus || 0,
          paymentDetails: {
            transactionId: milestone?.transactionId || `TXN-${Date.now()}`,
            paymentMethod: milestone?.paymentMethod || 'Direct deposit',
            processingTime: '2-3 business days'
          },
          projectDetails: {
            projectId: project?.id || project?.uuid || 'unknown',
            title: project?.title || 'Narration Project',
            hoursRecorded: milestone?.hoursRecorded || 'TBD',
            chaptersCompleted: milestone?.chaptersCompleted || milestone?.chapters?.length || 0
          }
        }
      })
    }
  },

  PROJECT_MILESTONE: {
    'quarter-complete': {
      messageType: 'PROJECT_MILESTONE',
      template: (project, progress, options = {}) => ({
        subject: `Project Update: ${project?.title || 'Narration Project'} - ${progress?.percentage || 0}% Complete`,
        content: `Project milestone update for ${project?.title || 'your narration project'}

**Progress:** ${progress?.percentage || 0}% complete
**Chapters Completed:** ${progress?.chaptersCompleted?.length > 0 ? progress.chaptersCompleted.join(', ') : 'Various chapters'}
**Hours Recorded:** ${progress?.hoursRecorded || 'TBD'} of ${project?.estimatedHours || project?.estimatedFinishedHours || 'TBD'}

**Quality Performance:**
- Average retake rate: ${((progress?.retakeRate || 0.05) * 100).toFixed(1)}% (Target: <15%)
- Character voice consistency: ${progress?.characterConsistency || 4.0}/5.0
- Emotional range delivery: ${progress?.emotionalDelivery || 4.0}/5.0
- Overall quality rating: ${progress?.overallQuality || 4.2}/5.0

**Timeline Status:**
- Current milestone: ${progress?.currentMilestone || 'In progress'}
- Estimated completion: ${progress?.estimatedCompletion || 'TBD'}
- On schedule: ${progress?.onSchedule !== false ? 'Yes' : 'No'}
- Days remaining: ${progress?.daysRemaining || 'TBD'}

**Upcoming Milestones:**
- Next chapters: ${progress?.upcomingChapters?.length > 0 ? progress.upcomingChapters.join(', ') : 'To be determined'}
- Next deadline: ${progress?.nextDeadline || 'TBD'}

**Technical Performance:**
- Audio quality: ${progress?.audioQuality || 4.0}/5.0
- Pronunciation accuracy: ${((progress?.pronunciationAccuracy || 0.95) * 100).toFixed(1)}%
- Consistency score: ${progress?.consistencyScore || 4.0}/5.0

${(progress?.specialChallenges?.length > 0) ? `**Special Challenges Handled:**\n${progress.specialChallenges.map(c => `- ${c}`).join('\n')}` : ''}

${(progress?.achievements?.length > 0) ? `**Notable Achievements:**\n${progress.achievements.map(a => `- ${a}`).join('\n')}` : ''}

${(progress?.feedback || progress?.notes) ? `**Additional Notes:**\n${progress.feedback || progress.notes}` : ''}

Great work so far! Keep up the excellent quality.

Best regards,
${options?.sender?.name || options?.explicitSender || 'Project Manager'}`,
        metadata: {
          projectId: project?.id || project?.uuid || 'unknown',
          completionPercentage: progress?.percentage || 0,
          qualityMetrics: {
            retakeRate: progress?.retakeRate || 0.05,
            characterConsistency: progress?.characterConsistency || 4.0,
            emotionalDelivery: progress?.emotionalDelivery || 4.0,
            overallQuality: progress?.overallQuality || 4.2,
            audioQuality: progress?.audioQuality || 4.0,
            pronunciationAccuracy: progress?.pronunciationAccuracy || 0.95,
            consistencyScore: progress?.consistencyScore || 4.0
          },
          timeline: {
            estimatedCompletion: progress?.estimatedCompletion || 'TBD',
            onSchedule: progress?.onSchedule !== false,
            currentMilestone: progress?.currentMilestone || 'In progress',
            daysRemaining: progress?.daysRemaining || 'TBD',
            nextDeadline: progress?.nextDeadline || 'TBD'
          },
          progress: {
            chaptersCompleted: progress?.chaptersCompleted || [],
            hoursRecorded: progress?.hoursRecorded || 'TBD',
            upcomingChapters: progress?.upcomingChapters || [],
            specialChallenges: progress?.specialChallenges || [],
            achievements: progress?.achievements || []
          },
          projectDetails: {
            title: project?.title || 'Narration Project',
            estimatedHours: project?.estimatedHours || project?.estimatedFinishedHours || 'TBD',
            genre: project?.genre || 'General',
            complexity: project?.complexity || 'Standard'
          }
        }
      })
    }
  }
};

// Export named exports for direct imports
export { MESSAGE_TYPES };

// Default export for existing compatibility
const BusinessMessageModels = {
  MESSAGE_TYPES,
  RATE_CALCULATIONS,
  TEST_USER_PROFILES,
  OPPORTUNITY_TEMPLATES,
  MESSAGE_TEMPLATES
};

export default BusinessMessageModels;
